import { toPng } from 'html-to-image';
import { z } from 'zod';

type ImageGenOptions = Parameters<typeof toPng>[1];

const DEFAULT_OPTIONS: ImageGenOptions = {
  pixelRatio: 2,
  skipAutoScale: true,
  style: {
    // Ensure no transformations from the parent affect the capture
    transform: 'none',
    transformOrigin: 'top left',
    // Ensure any animations are complete
    transition: 'none',
  },
};

export async function generateImageFromHtml(
  div: HTMLDivElement,
  width: number,
  height: number,
  options: ImageGenOptions = DEFAULT_OPTIONS
) {
  return toPng(div, {
    ...options,
    width,
    height,
  });
}

export function downloadItem(dataUrl: string, filename: string) {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  link.click();
}

const dataUrlSchema = z.string().refine(
  (val) => {
    const [prefix, base64] = val.split(',');
    return !!prefix && !!base64 && /^data:(.*?);base64$/.test(prefix);
  },
  {
    message: 'Invalid data URL format',
  }
);

export function base64ToBlob(dataUrl: string): Blob | undefined {
  const parsed = dataUrlSchema.safeParse(dataUrl);
  if (!parsed.success) return;

  const [prefix, base64] = parsed.data.split(',');
  if (!prefix || !base64) return;

  const match = prefix.match(/:(.*?);/);
  if (!match) return;

  const mime = match[1];
  const binary = atob(base64);
  const array = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }

  return new Blob([array], { type: mime });
}

export async function copyImageToClipboard(dataUrl: string): Promise<void> {
  try {
    const blob = base64ToBlob(dataUrl);
    if (!blob) throw new Error('Invalid image data URL');

    await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    throw error;
  }
}
